import { computed } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { Plan } from '@cognitoforms/types/server-types/plans/model/plan';
import type { UsageAnnualAverageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-annual-average-view-model';
import type { UsageItemViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-item-view-model';
import type { UsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-view-model';

const BYTES_PER_GB = 1e9;
export const GUESTS_MAX = 5000;
export const DOCUMENTS_MAX = 400;
export const STORAGE_MAX = 2000;
export const API_MAX = 2002000;

// Price-tier boundaries for block-priced add-ons (storage, API): the cost steps up by one
// block for every blockSize of usage beyond the included amount, so the slider draws a
// divider at each block boundary. Guarded against runaway counts when a block is tiny
// relative to the range.
const MAX_BLOCK_DIVIDERS = 60;
const blockDividers = (included: number, blockSize: number, max: number) => {
	if (blockSize <= 0 || (max - included) / blockSize > MAX_BLOCK_DIVIDERS)
		return [];

	const dividers: number[] = [];
	for (let edge = included + blockSize; edge < max; edge += blockSize)
		dividers.push(edge);
	return dividers;
};

export interface WalletQuantities {
	users?: number;
	guests: number;
	documentGeneration: number;
	storage: number;
	apiUsage: number;
	additionalFunds: number;
}

export interface UseWalletAddOnsOptions {
	plan: Ref<Plan | null> | ComputedRef<Plan | null>;
	currentUsage: Ref<UsageViewModel | null>;
	usageAverages: Ref<UsageAnnualAverageViewModel | null>;
	quantities: WalletQuantities;
	// Per-billing-period multiplier applied to recurring overage costs: the checkout flow
	// commits whole annual months, while the refill dialog charges for the invoices left
	// before the annual plan renews.
	multiplier: Ref<number> | ComputedRef<number>;
	// The checkout flow lets the user change their user count, so it surfaces the Users row
	// in the historical-usage breakdown; the refill dialog keeps the plan fixed and omits it.
	includeUsers?: boolean;
	// Additional seats already prepaid in the current annual term. Seats within this
	// commitment are funded by the prior prepayment, so userTotal only reflects the net
	// change: a charge for seats beyond it or a credit for committed seats the new plan
	// now includes.
	committedUsers?: Ref<number> | ComputedRef<number>;
	// The refill dialog warns when a selection falls below historical usage; the checkout
	// flow does not.
	withWarnings?: boolean;
}

export function useWalletAddOns(options: UseWalletAddOnsOptions) {
	const { plan, currentUsage, usageAverages, quantities, multiplier } = options;
	const includeUsers = options.includeUsers ?? false;
	const withWarnings = options.withWarnings ?? false;

	const storageLimitGb = computed(() => (plan.value?.StorageLimit ?? 0) / BYTES_PER_GB);
	const storageBlockSizeGb = computed(() => (plan.value?.AdditionalStorageFeeBlockSize ?? 0) / BYTES_PER_GB);

	const storageUsageGb = (item: UsageItemViewModel) => item.Units === 'MB' ? (item.CurrentUsage ?? 0) / 1000 : (item.CurrentUsage ?? 0);

	const liveDocumentTemplateCount = () => {
		const formsUsage = currentUsage.value?.FormsUsage;
		if (!formsUsage)
			return null;

		return formsUsage.reduce((total, form) => total + form.DocumentTemplates, 0);
	};

	const currentUsageFor = (name: string) => {
		const item = currentUsage.value?.Items?.find(i => i.Name === name);

		if (name === 'Document Templates')
			return liveDocumentTemplateCount() ?? item?.CurrentUsage ?? 0;

		if (!item)
			return 0;

		if (name === 'Storage')
			return storageUsageGb(item);

		return item.CurrentUsage ?? 0;
	};

	// Floor these add-ons at current usage: an org can't drop below its existing users,
	// guests, document templates, or stored data, so a lower selection would be immediately
	// over capacity.
	const addOnMinimums = computed(() => ({
		users: Math.max(plan.value?.UserLimit ?? 0, Math.ceil(currentUsageFor('Users'))),
		guests: Math.max(plan.value?.IncludedGuests ?? 0, Math.ceil(currentUsageFor('Guests'))),
		documents: Math.max(plan.value?.CustomTemplateLimit ?? 0, Math.ceil(currentUsageFor('Document Templates'))),
		storageGb: Math.max(storageLimitGb.value, Math.ceil(currentUsageFor('Storage')))
	}));

	const monthlyAverages = computed(() => ({
		users: usageAverages.value?.AverageUsers ?? 0,
		guests: usageAverages.value?.AverageGuests ?? 0,
		documents: usageAverages.value?.AverageDocumentTemplates ?? 0,
		storageGb: (usageAverages.value?.AverageStorageBytes ?? 0) / BYTES_PER_GB,
		apiUsage: usageAverages.value?.AverageApiUsage ?? 0
	}));

	const suggestedUsage = computed(() => ({
		users: Math.max(Math.ceil(monthlyAverages.value.users), addOnMinimums.value.users),
		guests: Math.max(Math.ceil(monthlyAverages.value.guests), addOnMinimums.value.guests),
		documents: Math.max(Math.ceil(monthlyAverages.value.documents), addOnMinimums.value.documents),
		storageGb: Math.max(Math.ceil(monthlyAverages.value.storageGb), addOnMinimums.value.storageGb),
		apiUsage: Math.max(Math.ceil(monthlyAverages.value.apiUsage), plan.value?.ApiUsageLimit ?? 0)
	}));

	// Mirrors GuestPricingPlan.GetBucket (Cognito.Core/Model/Plans/GuestPricingPlan.cs):
	// the first bucket covering the count sets the price; beyond the largest bucket,
	// AdditionalBucketCost is added per AdditionalBucketSize chunk.
	const guestPricing = (value: number) => {
		const pricing = plan.value?.GuestPricing;
		if (value <= 0 || !pricing?.Buckets?.length)
			return 0;

		const bucket = pricing.Buckets.find(b => value <= b.Limit);
		if (bucket)
			return bucket.Price;

		const largest = pricing.Buckets[pricing.Buckets.length - 1];
		return largest.Price + Math.ceil((value - largest.Limit) / pricing.AdditionalBucketSize) * pricing.AdditionalBucketCost;
	};

	// Boundaries where the guest price steps up: the upper limit of each named bucket, then
	// every AdditionalBucketSize chunk beyond the largest bucket up to the slider max. The
	// slider renders these as dividers so the price tiers are visible.
	const guestBucketDividers = computed(() => {
		const pricing = plan.value?.GuestPricing;
		if (!pricing?.Buckets?.length)
			return [];

		const dividers = pricing.Buckets.map(b => b.Limit);
		if (pricing.AdditionalBucketSize > 0) {
			const largest = pricing.Buckets[pricing.Buckets.length - 1].Limit;
			for (let edge = largest + pricing.AdditionalBucketSize; edge < GUESTS_MAX; edge += pricing.AdditionalBucketSize)
				dividers.push(edge);
		}
		return dividers;
	});

	const addOnPricingData = computed(() => ({
		users: {
			pricing: plan.value?.AdditionalUserFee ?? 0,
			included: plan.value?.UserLimit ?? 0
		},
		guests: {
			pricing: guestPricing,
			included: plan.value?.IncludedGuests ?? 0
		},
		documents: {
			pricing: plan.value?.AdditionalTemplateFee ?? 0,
			included: plan.value?.CustomTemplateLimit ?? 0
		},
		storage: {
			pricing: plan.value?.AdditionalStorageFee ?? 0,
			pricingPer: storageBlockSizeGb.value,
			included: storageLimitGb.value
		},
		api: {
			pricing: plan.value?.ApiUsageFee ?? 0,
			pricingPer: plan.value?.ApiUsageFeeBlockSize ?? 0,
			included: plan.value?.ApiUsageLimit ?? 0
		}
	}));

	// Only offer wallet add-ons the plan actually prices (e.g. only Enterprise defines
	// storage overage fees, and free plans have no guest pricing).
	const showGuestsAddOn = computed(() => !!plan.value?.AllowGuests && !!plan.value?.GuestPricing?.Buckets?.length);
	const showDocumentsAddOn = computed(() => addOnPricingData.value.documents.pricing > 0);
	const showStorageAddOn = computed(() => addOnPricingData.value.storage.pricing > 0 && addOnPricingData.value.storage.pricingPer > 0);
	const showApiAddOn = computed(() => addOnPricingData.value.api.pricing > 0 && addOnPricingData.value.api.pricingPer > 0);

	const walletAddOns = computed(() => [
		{
			key: 'guests',
			show: showGuestsAddOn.value,
			model: 'guests' as const,
			props: {
				title: 'Guests',
				label: 'guests',
				icon: 'complex-icons/person-field',
				infoDialog: true,
				min: addOnMinimums.value.guests,
				max: GUESTS_MAX,
				recommendedValue: suggestedUsage.value.guests,
				step: 50,
				buckets: guestBucketDividers.value,
				pricing: addOnPricingData.value.guests.pricing,
				included: addOnPricingData.value.guests.included,
				...(withWarnings ? {
					warningThreshold: Math.ceil(monthlyAverages.value.guests),
					warningMessage: `Your organization has averaged ${Math.ceil(monthlyAverages.value.guests).toLocaleString('en-us')} guests per month. Paying for less than this could result in running out of your usage wallet before your annual plan renews.`
				} : {})
			}
		},
		{
			key: 'documents',
			show: showDocumentsAddOn.value,
			model: 'documentGeneration' as const,
			props: {
				title: 'Document Generation',
				label: 'documents',
				singularLabel: 'document',
				icon: 'complex-icons/document-generation',
				info: 'Additional Custom Documents are $4/template/month.',
				min: addOnMinimums.value.documents,
				max: DOCUMENTS_MAX,
				recommendedValue: suggestedUsage.value.documents,
				pricing: addOnPricingData.value.documents.pricing,
				included: addOnPricingData.value.documents.included
			}
		},
		{
			key: 'storage',
			show: showStorageAddOn.value,
			model: 'storage' as const,
			props: {
				title: 'Storage',
				label: 'GB',
				icon: 'complex-icons/data-collection',
				info: 'Get additional storage for $25/month per 50 GB over your plan limit.',
				min: addOnMinimums.value.storageGb,
				max: STORAGE_MAX,
				recommendedValue: suggestedUsage.value.storageGb,
				step: storageBlockSizeGb.value,
				buckets: blockDividers(addOnPricingData.value.storage.included, storageBlockSizeGb.value, STORAGE_MAX),
				pricing: addOnPricingData.value.storage.pricing,
				pricingPer: addOnPricingData.value.storage.pricingPer,
				included: addOnPricingData.value.storage.included
			}
		},
		{
			key: 'api',
			show: showApiAddOn.value,
			model: 'apiUsage' as const,
			props: {
				title: 'API Usage',
				label: 'requests',
				icon: 'complex-icons/api',
				info: 'Get additional API Requests at $20 per 100,000 requests/month.',
				min: plan.value?.ApiUsageLimit ?? 0,
				max: API_MAX,
				recommendedValue: suggestedUsage.value.apiUsage,
				step: plan.value?.ApiUsageFeeBlockSize ?? 0,
				buckets: blockDividers(addOnPricingData.value.api.included, plan.value?.ApiUsageFeeBlockSize ?? 0, API_MAX),
				pricing: addOnPricingData.value.api.pricing,
				pricingPer: addOnPricingData.value.api.pricingPer,
				included: addOnPricingData.value.api.included,
				...(withWarnings ? {
					warningThreshold: Math.ceil(monthlyAverages.value.apiUsage),
					warningMessage: `Your organization has averaged ${Math.ceil(monthlyAverages.value.apiUsage).toLocaleString('en-us')} API requests per month. Paying for less than this could result in running out of your usage wallet before your annual plan renews.`
				} : {})
			}
		}
	].filter(card => card.show));

	const averageRows = computed(() => [
		{ icon: 'users', label: 'Users', value: monthlyAverages.value.users, decimals: 0, suffix: '', show: includeUsers },
		{ icon: 'person', label: 'Guests', value: monthlyAverages.value.guests, decimals: 0, suffix: '', show: showGuestsAddOn.value },
		{ icon: 'generated-documents', label: 'Documents', value: monthlyAverages.value.documents, decimals: 0, suffix: '', show: showDocumentsAddOn.value },
		{ icon: 'data', label: 'Storage', value: monthlyAverages.value.storageGb, decimals: 1, suffix: ' GB', show: showStorageAddOn.value },
		{ icon: 'api', label: 'API Usage', value: monthlyAverages.value.apiUsage, decimals: 0, suffix: '', show: showApiAddOn.value }
	].filter(row => row.show));

	const usageWindowLabel = computed(() => {
		const averages = usageAverages.value;
		if (!averages?.FirstMonth || !averages?.LastMonth)
			return '';

		const formatMonth = (date: Date) => date.toLocaleString('en-us', { month: 'long', year: 'numeric', timeZone: 'UTC' });
		return `${formatMonth(new Date(averages.FirstMonth))} - ${formatMonth(new Date(averages.LastMonth))}`;
	});

	const formatAverage = (value: number, decimals = 0) => value.toLocaleString('en-us', { maximumFractionDigits: decimals });

	const userTotal = computed(() => {
		const additional = Math.max(0, (quantities.users ?? 0) - addOnPricingData.value.users.included);
		return (additional - (options.committedUsers?.value ?? 0)) * addOnPricingData.value.users.pricing * multiplier.value;
	});

	const walletTotal = computed(() => {
		const data = addOnPricingData.value;
		const periods = multiplier.value;
		let total = quantities.additionalFunds;
		if (showGuestsAddOn.value)
			total += guestPricing(Math.max(quantities.guests - data.guests.included, 0)) * periods;
		if (showDocumentsAddOn.value)
			total += Math.max(quantities.documentGeneration - data.documents.included, 0) * data.documents.pricing * periods;
		if (showStorageAddOn.value)
			total += Math.ceil(Math.max(quantities.storage - data.storage.included, 0) / data.storage.pricingPer) * data.storage.pricing * periods;
		if (showApiAddOn.value)
			total += Math.ceil(Math.max(quantities.apiUsage - data.api.included, 0) / data.api.pricingPer) * data.api.pricing * periods;
		return total;
	});

	return {
		storageLimitGb,
		storageBlockSizeGb,
		currentUsageFor,
		addOnMinimums,
		monthlyAverages,
		suggestedUsage,
		guestPricing,
		addOnPricingData,
		showGuestsAddOn,
		showDocumentsAddOn,
		showStorageAddOn,
		showApiAddOn,
		walletAddOns,
		averageRows,
		usageWindowLabel,
		formatAverage,
		userTotal,
		walletTotal
	};
}
